'use client';

import { useCallback, useMemo, useState } from 'react';
import { map, size, orderBy, find } from 'lodash';
import compactFP from 'lodash/fp/compact';
import filterFP from 'lodash/fp/filter';
import flatMapFP from 'lodash/fp/flatMap';
import flowFP from 'lodash/fp/flow';
import mapFP from 'lodash/fp/map';
import orderByFP from 'lodash/fp/orderBy';
import uniqByFP from 'lodash/fp/uniqBy';
import { matchSorter } from 'match-sorter';
import { useMediaQuery } from 'usehooks-ts';
import * as Accordion from '@/components/ui/Accordion';
import { ScrollLayout } from '@/components/ui/ScrollArea';
import type { Action, Contact, GeneralConditionSubGroup } from '@/graphql/common/types';
import type { GeneralConditionProduct } from '@/graphql/queries/pages/condicoes-gerais/types';
import { Select, Input } from '@axa/design-system';
import { GeneralConditionItem } from './GeneralConditionItem';
import { Sidebar } from './Sidebar';

type GeneralConditionProps = {
  homeGeneralCondition?: {
    title: string;
    description?: string;
    contact?: Contact;
    action?: Action;
  };
  products?: GeneralConditionProduct[];
  insurances?: {
    title: string;
    slug: string;
  }[];
  hasInsurance?: boolean;
};

type Option = {
  label: string;
  value: string | number;
};

export const GeneralCondition = ({
  homeGeneralCondition,
  products,
  insurances,
  hasInsurance
}: GeneralConditionProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const [selectedProduct, setSelectedProduct] = useState<Option | undefined>();
  const [searchGeneralCondition, setSearchGeneralCondition] = useState('');

  const generalConditionSubGroups = useMemo(() => {
    return flowFP(
      flatMapFP((product: GeneralConditionProduct) => product?.generalConditionList ?? []),
      uniqByFP(
        (generalConditionSubGroup: GeneralConditionSubGroup) => generalConditionSubGroup?.slug
      )
    )(products);
  }, [products]);

  const filteredGeneralCondition = useMemo(() => {
    if (!products || !hasInsurance) {
      if (searchGeneralCondition) {
        setSearchGeneralCondition('');
      }

      if (selectedProduct) {
        setSelectedProduct(undefined);
      }

      return [];
    }

    let filtered: GeneralConditionSubGroup[] = generalConditionSubGroups;

    if (selectedProduct?.value && !searchGeneralCondition) {
      const filteredProduct = find(products, (product) => product.slug === selectedProduct.value);

      if (filteredProduct) {
        filtered = filteredProduct?.generalConditionList ?? [];
      }
    }

    if (searchGeneralCondition) {
      const filteredSubGroupCards = flowFP(
        mapFP((generalConditionSubGroup: GeneralConditionSubGroup) => {
          const filteredCards = matchSorter(
            generalConditionSubGroup.cardGeneralCondition,
            searchGeneralCondition,
            {
              threshold: matchSorter.rankings.CONTAINS,
              keys: ['title', 'document']
            }
          );

          return {
            ...generalConditionSubGroup,
            cardGeneralCondition: filteredCards,
            hasFilteredCards: size(filteredCards) > 0
          };
        }),
        filterFP((generalConditionSubGroup) => generalConditionSubGroup.hasFilteredCards)
      )(generalConditionSubGroups);

      if (size(filteredSubGroupCards) > 0) {
        filtered = filteredSubGroupCards;
      } else {
        filtered = matchSorter(generalConditionSubGroups, searchGeneralCondition, {
          threshold: matchSorter.rankings.CONTAINS,
          keys: ['title', 'document']
        });
      }
    }

    const orderedFilteredSubGroups = orderBy(filtered, ['title'], 'asc');

    return orderedFilteredSubGroups;
  }, [products, generalConditionSubGroups, selectedProduct, searchGeneralCondition, hasInsurance]);

  const productOptions = useMemo(() => {
    if (!products) return [];

    const options = flowFP(
      mapFP((product: { title?: string; slug?: string }) => {
        if (product) {
          return {
            label: product?.title,
            value: product?.slug
          };
        }

        return null;
      }),
      compactFP,
      orderByFP(['label'], ['asc'])
    )(products) as Option[];

    return options;
  }, [products]);

  const handleClearSearch = useCallback(() => {
    setSearchGeneralCondition('');
    setSelectedProduct(undefined);
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[30vw_1fr]">
      <Sidebar
        title={homeGeneralCondition?.title}
        description={homeGeneralCondition?.description}
        contact={homeGeneralCondition?.contact}
        action={homeGeneralCondition?.action}
        insurances={insurances}
        handleClearSearch={handleClearSearch}
      />

      <section className="flex size-full flex-col px-6 pb-10 lg:min-h-[calc(100vh_-_168px)] lg:px-12 lg:pb-6 xl:min-h-[calc(100vh_-_176px)]">
        <div className="flex flex-1 flex-col gap-4 pb-6 pt-10 lg:flex-row lg:px-1 lg:py-6 2xl:pb-10 2xl:pt-12">
          <Input
            leftIcon={{
              name: 'Search'
            }}
            placeholder="Digite o nome do produto ou o número SUSEP para encontrar os documentos"
            autoComplete="off"
            onChange={(event) => setSearchGeneralCondition(event.target.value)}
            value={searchGeneralCondition}
            disabled={size(generalConditionSubGroups) === 0 || !hasInsurance}
          />
          <Select
            searchable={false}
            placeholder="Selecione um produto"
            options={productOptions}
            onChange={setSelectedProduct}
            value={selectedProduct}
            disabled={size(generalConditionSubGroups) === 0 || !hasInsurance}
          />
        </div>

        <ScrollLayout enabled={!isMobile} className="size-full" thumbClassName="!z-[5]" type="auto">
          {size(filteredGeneralCondition) > 0 ? (
            <Accordion.Root type="single" collapsible>
              {map(filteredGeneralCondition, (generalCondition) => (
                <Accordion.Item
                  key={generalCondition.slug}
                  value={generalCondition.slug}
                  className="lg:pr-1"
                >
                  <Accordion.Trigger className="h-20 py-6 text-left text-xl font-semibold normal-case text-grey-900 shadow-grey-700 focus-within:outline-1 focus-within:outline-grey-700">
                    {generalCondition?.title ?? 'Não informado'}
                  </Accordion.Trigger>
                  <Accordion.Content className="py-6">
                    <ul className="flex flex-col gap-4 divide-y divide-grey-300">
                      {map(generalCondition.cardGeneralCondition, (card) => (
                        <GeneralConditionItem key={card.id} item={card} className="py-6" />
                      ))}
                    </ul>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          ) : (
            <span className="py-6 text-base text-grey-700">
              {hasInsurance
                ? 'Nenhuma condição geral encontrada'
                : 'Selecione uma opção na barra lateral para iniciar sua busca.'}
            </span>
          )}
        </ScrollLayout>
      </section>
    </div>
  );
};
